/**
 * Wallet creation orchestration hook.
 *
 * State machine: idle -> generating -> pin_setup -> encrypting ->
 * biometric_offer -> finalizing -> complete.
 *
 * Orchestrates: mnemonic generation, BIP-85 derivation, Evolu init,
 * Nostr identity seeding, PIN encryption, SecureStorage write,
 * and auth state transition.
 *
 * SECURITY: Never logs mnemonics, PINs, or private key material.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module useCreateWallet
 */

import { useCallback, useRef, useState } from 'react';
import { useEvolu } from '@evolu/react';
import { generateMnemonic, deriveChildMnemonic } from '@/lib/crypto';
import { encryptMnemonic } from '@/lib/crypto-encrypt';
import { restoreWithMnemonic } from '@/db/database';
import { createPlatformServices } from '@/lib/platform/index';
import { useMnemonic } from '@/contexts/MnemonicProvider';
import { useAuth } from '@/contexts/AuthProvider';
import { useBiometric } from '@/features/auth/hooks/useBiometric';
import {
  typedEvolu,
  seedNostrIdentity,
  seedDefaultCategories,
  seedDefaultMint,
  setOnboardingComplete,
} from '@/features/onboarding/utils/wallet-seed-helpers';

/** Wallet creation states. */
export type CreateWalletState =
  | 'idle'
  | 'generating'
  | 'pin_setup'
  | 'encrypting'
  | 'biometric_offer'
  | 'finalizing'
  | 'complete';

/** Return value from useCreateWallet. */
export interface UseCreateWalletResult {
  /** Current state of the creation flow. */
  readonly state: CreateWalletState;
  /** Error message if something went wrong. */
  readonly error: string | null;
  /** Begin wallet creation (generates mnemonic, inits Evolu, seeds data). */
  startCreation(): Promise<void>;
  /** Set the PIN and encrypt the mnemonic. */
  setPinAndFinalize(pin: string): Promise<void>;
  /** Skip the biometric setup. */
  skipBiometric(): void;
  /**
   * Enable biometric authentication during the biometric_offer state.
   * Stores the PIN encrypted for biometric unlock via useBiometric().enable(pin).
   *
   * @param pin - The PIN to store for biometric unlock
   */
  enableBiometric(pin: string): Promise<void>;
}

/** BIP-85 index for Evolu child mnemonic (per ADR-014). */
const EVOLU_CHILD_INDEX = 0;

/** SecureStorage key for encrypted mnemonic blob. */
const ENCRYPTED_MNEMONIC_KEY = 'encrypted_mnemonic';

/**
 * Hook that orchestrates the full wallet creation flow.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @returns State machine controls for the creation flow
 */
export function useCreateWallet(): UseCreateWalletResult {
  const [state, setState] = useState<CreateWalletState>('idle');
  const [error, setError] = useState<string | null>(null);
  const mnemonicRef = useRef<string | null>(null);
  const pinRef = useRef<string | null>(null);
  const evolu = useEvolu();
  const { unlock: mnemonicUnlock } = useMnemonic();
  const { completeOnboarding } = useAuth();
  const biometric = useBiometric();

  const startCreation = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      setState('generating');

      // Step 1: Generate master mnemonic
      const mnemonic = generateMnemonic();
      mnemonicRef.current = mnemonic;

      // Step 2: Derive Evolu child mnemonic and initialize Evolu
      const evoluChildMnemonic = deriveChildMnemonic(mnemonic, EVOLU_CHILD_INDEX);
      await restoreWithMnemonic(typedEvolu(evolu), evoluChildMnemonic);

      // Step 3: Seed Nostr identity
      seedNostrIdentity(evolu, mnemonic);

      // Step 4: Seed default categories
      seedDefaultCategories(evolu);

      // Step 5: Configure default Cashu mint
      seedDefaultMint(evolu);

      // Transition to PIN setup
      setState('pin_setup');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Wallet creation failed';
      setError(message);
      setState('idle');
    }
  }, [evolu]);

  const setPinAndFinalize = useCallback(async (pin: string): Promise<void> => {
    const mnemonic = mnemonicRef.current;
    if (!mnemonic) {
      setError('No mnemonic available. Please start again.');
      setState('idle');
      return;
    }

    try {
      setState('encrypting');

      // Step 6: Encrypt mnemonic with PIN
      const encrypted = await encryptMnemonic(mnemonic, pin);

      // Step 7: Store encrypted blob in SecureStorage
      const services = await createPlatformServices();
      await services.secureStorage.store(ENCRYPTED_MNEMONIC_KEY, encrypted);

      // Store PIN ref for biometric enrollment
      pinRef.current = pin;

      // Step 8: Transition to biometric offer
      setState('biometric_offer');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to encrypt wallet';
      setError(message);
      setState('pin_setup');
    }
  }, []);

  const skipBiometric = useCallback((): void => {
    const mnemonic = mnemonicRef.current;
    if (!mnemonic) {
      setError('No mnemonic available. Please start again.');
      setState('idle');
      return;
    }

    setState('finalizing');
    setOnboardingComplete(evolu);
    mnemonicUnlock(mnemonic);
    completeOnboarding();
    mnemonicRef.current = null;
    setState('complete');
  }, [evolu, mnemonicUnlock, completeOnboarding]);

  const enableBiometric = useCallback(
    async (pin: string): Promise<void> => {
      try {
        await biometric.enable(pin);
      } catch {
        // Biometric enrollment failure is non-fatal; onboarding can continue
      }

      const mnemonic = mnemonicRef.current;
      if (!mnemonic) {
        setError('No mnemonic available. Please start again.');
        setState('idle');
        return;
      }

      setState('finalizing');
      setOnboardingComplete(evolu);
      mnemonicUnlock(mnemonic);
      completeOnboarding();
      mnemonicRef.current = null;
      pinRef.current = null;
      setState('complete');
    },
    [biometric, evolu, mnemonicUnlock, completeOnboarding],
  );

  return {
    state,
    error,
    startCreation,
    setPinAndFinalize,
    skipBiometric,
    enableBiometric,
  };
}
